import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useUser } from "@/redux/user";
import { api } from "@/services/api";
import PracticeBoard, { PuzzleAction } from "./PracticeBoard";
import SingleMistakePractice from "./SingleMistakePractice";
import useTranslation from "next-translate/useTranslation";

type PersonalPuzzle = {
  id: string;
  fen: string;
  gameId: string;
  gameUrl: string;
  moveIndex: number;
  done: boolean;
};

export default function MistakeTrainer() {
  const user = useUser();
  const router = useRouter();
  const session = router.query.session;
  const id = router.query.id;
  if (
    user.memberLoggedIn &&
    typeof id === "string" &&
    typeof session === "string" &&
    /^[a-f0-9]{32}$/.test(session)
  )
    return (
      <SingleMistakePractice
        key={`${user.id}-${id}-${session}`}
        puzzleId={id}
        practiceSessionId={session}
        entrySource={
          router.query.source === "email_review"
            ? "email_review"
            : "game_review"
        }
      />
    );
  return <AccountMistakes key={user.id || "guest"} />;
}

function AccountMistakes() {
  const { t } = useTranslation("puzzles");
  const user = useUser();
  const router = useRouter();
  const [puzzles, setPuzzles] = useState<PersonalPuzzle[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [reviewAgain, setReviewAgain] = useState(false);
  const [latestReviewedGameUrl, setLatestReviewedGameUrl] = useState<
    string | null
  >(null);
  const [total, setTotal] = useState(0);
  const [solved, setSolved] = useState(0);
  const [reviewed, setReviewed] = useState<string[]>([]);
  const [moveComparison, setMoveComparison] = useState<{
    playedMove?: string;
    bestMove?: string;
  }>({});
  const [explanation, setExplanation] = useState("");
  const requestVersion = useRef(0);
  const gameId =
    typeof router.query.gameId === "string" ? router.query.gameId : undefined;
  const puzzleId =
    typeof router.query.id === "string" ? router.query.id : undefined;
  const puzzle = puzzles[0];

  async function load(nextCursor?: number, forceAll = reviewAgain) {
    const version = ++requestVersion.current;
    setLoading(true);
    setError("");
    setExplanation("");
    setPending(false);
    try {
      if (gameId && !nextCursor) {
        const generated = await api.post(
          "/review-puzzles/generate",
          { gameId },
          { timeout: 15000 },
        );
        if (version !== requestVersion.current) return;
        setPending(!!generated.data.pending);
      }
      if (puzzleId) {
        const response = await api.get(
          `/review-puzzles/${encodeURIComponent(puzzleId)}`,
          { timeout: 15000 },
        );
        if (version !== requestVersion.current) return;
        setPuzzles([response.data.puzzle]);
        setCursor(null);
      } else {
        const response = await api.get("/review-puzzles", {
          params: {
            cursor: nextCursor,
            includeSolved: forceAll ? "true" : undefined,
            gameId,
          },
          timeout: 15000,
        });
        if (version !== requestVersion.current) return;
        setPuzzles(response.data.puzzles);
        setCursor(response.data.nextCursor);
        setTotal(response.data.total);
        setLatestReviewedGameUrl(response.data.latestReviewedGameUrl || null);
        setSolved(response.data.solved);
      }
      setLoaded(true);
    } catch {
      if (version === requestVersion.current)
        setError(
          puzzleId
            ? t("mistakes.openError")
            : t("mistakes.loadError"),
        );
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }

  useEffect(() => {
    if (user.memberLoggedIn && router.isReady) {
      setPuzzles([]);
      setLoaded(false);
      void load();
    }
    return () => {
      requestVersion.current++;
    };
  }, [user.memberLoggedIn, router.isReady, puzzleId, gameId, reviewAgain]);

  async function action(input: PuzzleAction) {
    const response = await api.post(
      `/review-puzzles/${puzzle.id}/attempt`,
      input,
      { timeout: 15000 },
    );
    setExplanation(response.data.explanation || "");
    if (response.data.done || response.data.failed) {
      setReviewed((ids) =>
        ids.includes(puzzle.id) ? ids : [...ids, puzzle.id],
      );
      setMoveComparison({
        playedMove: response.data.playedMove,
        bestMove: response.data.bestMove,
      });
    }
    if (response.data.done && !puzzle.done) {
      setSolved((count) => count + 1);
      setPuzzles((list) =>
        list.map((item) =>
          item.id === puzzle.id ? { ...item, done: true } : item,
        ),
      );
    }
    return response.data;
  }

  function next() {
    setMoveComparison({});
    setExplanation("");
    if (puzzleId) {
      void router.push("/puzzles/mistakes/practice");
      return;
    }
    if (puzzles.length > 1) setPuzzles((list) => list.slice(1));
    else if (cursor) void load(cursor);
    else setPuzzles([]);
  }

  if (!user.memberLoggedIn)
    return (
      <div className="mx-auto max-w-xl rounded-lg bg-chess-ui-panel p-6">
        <h2 className="text-xl font-semibold text-slate-900">
          {t("mistakes.privateTitle")}
        </h2>
        <p className="mt-2 text-base text-slate-600">
          {t("mistakes.privateText")}
        </p>
        <div className="mt-4 flex gap-4">
          <Button asChild variant="default">
            <Link href="/signIn">{t("mistakes.signIn")}</Link>
          </Button>
          <Button asChild variant="neutral">
            <Link href="/register">{t("mistakes.createAccount")}</Link>
          </Button>
        </div>
      </div>
    );
  return (
    <>
      {loading && (
        <p role="status" className="my-4 text-slate-600">
          {t("mistakes.loading")}
        </p>
      )}
      {error && (
        <p role="alert" className="my-4 text-red-700">
          {error}{" "}
          <Button
            variant="quiet"
            size="default"
            type="button"
            onClick={() => load()}
          >
            {t("general.retry")}
          </Button>
        </p>
      )}
      {pending && (
        <div className="mb-5 rounded-lg border border-athens-gray-200 bg-white p-5">
          <p>
            {t("mistakes.reviewPending")}
          </p>
          <Button asChild variant="link" className="mt-3">
            <Link
              href={`/game-review/${encodeURIComponent(gameId!)}/${user.id}`}
            >
              {t("mistakes.openReview")}
            </Link>
          </Button>{" "}
          <Button
            variant="quiet"
            size="default"
            type="button"
            className="ml-4"
            onClick={() => load()}
          >
            {t("mistakes.refresh")}
          </Button>
        </div>
      )}
      {puzzle && !loading && (
        <PracticeBoard
          key={`${puzzle.id}-${reviewAgain}`}
          puzzle={puzzle}
          layout="mistakes"
          nextLabel={t("mistakes.nextMistake")}
          onAction={action}
          onNext={next}
          explanation={explanation}
        >
          <p className="mt-5 text-sm font-semibold text-slate-600">
            {t("mistakes.reviewedSession", { count: reviewed.length })}
          </p>
          {moveComparison.bestMove && (
            <p className="mt-3 text-sm text-slate-600">
              {moveComparison.playedMove && (
                <>{t("mistakes.inGame", { move: moveComparison.playedMove })}{" "}</>
              )}
              {t("mistakes.betterMove", { move: moveComparison.bestMove })}
            </p>
          )}
          <p className="mt-5 border-t border-athens-gray-200 pt-4 text-sm text-slate-500">
            {t("mistakes.source", { move: Math.ceil(puzzle.moveIndex / 2) })}
          </p>
          <Button asChild variant="link" className="mt-2">
            <Link href={puzzle.gameUrl}>{t("mistakes.reviewOriginal")}</Link>
          </Button>
        </PracticeBoard>
      )}
      {loaded && !loading && !puzzle && !error && (
        <div className="mx-auto max-w-xl rounded-lg bg-chess-ui-panel p-6">
          <h2 className="text-xl font-semibold text-slate-900">
            {total
              ? t("mistakes.queueEnd")
              : latestReviewedGameUrl
                ? t("mistakes.noSuitable")
                : t("mistakes.noneYet")}
          </h2>
          <p className="mt-3 max-w-xl text-base leading-6 text-slate-600">
            {total
              ? t("mistakes.queueEndText")
              : latestReviewedGameUrl
                ? t("mistakes.noSuitableText")
                : t("mistakes.noneYetText")}
          </p>
          <div className="mt-5 flex flex-wrap gap-4">
            <Button asChild variant="default">
              <Link href={latestReviewedGameUrl || "/profile"}>
                {latestReviewedGameUrl ? t("mistakes.openReview") : t("mistakes.reviewGame")}
              </Link>
            </Button>
            <Button asChild variant="neutral">
              <Link href="/puzzles/easy">
                {t("mistakes.easyPuzzles")}
              </Link>
            </Button>
            {total > 0 && (
              <Button
                variant="quiet"
                size="default"
                type="button"
                onClick={() => {
                  if (reviewAgain) void load(undefined, true);
                  else setReviewAgain(true);
                }}
              >
                {t("mistakes.previousPuzzles")}
              </Button>
            )}
            <Button
              variant="quiet"
              size="default"
              type="button"
              onClick={() => load()}
            >
              {t("mistakes.refreshPractice")}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
